import React from 'react'
import { isNumber } from '@mote/helpers/assertion'
import { __DEV__ } from '@mote/helpers/constants'
import { mergeClassnames } from '@mote/helpers/dom'
import { useIsFirstRender, usePrevious } from '@mote/helpers/hooks'
import styled, { css, keyframes, useColor, x } from '@xstyled/styled-components'
import type { HTMLXStyledProps } from '../../system'
import { forwardRef } from '../../system'

export interface SkeletonOptions {
  startColor?: string
  endColor?: string
  isLoaded?: boolean
  speed?: number
  fadeDuration?: number
  fadeSpeed?: number
}

export interface SkeletonProps
  extends HTMLXStyledProps<'div'>,
    SkeletonOptions {}

const fadeOpacity = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 }
})

const fadeColor = (startColor: string, endColor: string) =>
  keyframes({
    from: {
      borderColor: startColor,
      backgroundColor: startColor
    },
    to: { borderColor: endColor, backgroundColor: endColor }
  })

function range(count: number) {
  return Array(count)
    .fill(1)
    .map((_, index) => index + 1)
}

function evaluateValue(value: SkeletonProps['w']) {
  return isNumber(value) ? `${value}px` : value
}

export const Skeleton = forwardRef<SkeletonProps, 'div'>((props, ref) => {
  const {
    startColor: startColorProp = 'grey.l2',
    endColor: endColorProp = 'grey.d1',
    isLoaded,
    fadeDuration = 0.4,
    fadeSpeed = 0.8,
    className,
    ...rest
  } = props
  const startColor = useColor(startColorProp)
  const endColor = useColor(endColorProp)
  const isFirstRender = useIsFirstRender()

  const wasPreviouslyLoaded = usePrevious(isLoaded)

  const _className = mergeClassnames('mote-skeleton', className)

  if (isLoaded) {
    const animation =
      isFirstRender || wasPreviouslyLoaded
        ? 'none'
        : css`
            ${fadeOpacity} ${fadeDuration}s
          `

    return (
      <SkeletonLoadedRoot
        ref={ref}
        className={_className}
        {...rest}
        animation={animation}
      />
    )
  }

  return (
    <SkeletonRoot
      ref={ref}
      className={_className}
      borderRadius='2px'
      borderColor={startColor}
      backgroundColor={endColor}
      startColor={startColor as string}
      endColor={endColor as string}
      fadeSpeed={fadeSpeed}
      {...rest}
    />
  )
})

if (__DEV__) {
  Skeleton.displayName = 'Skeleton'
}

export interface SkeletonTextProps extends SkeletonProps {
  spacing?: SkeletonProps['margin']
  skeletonHeight?: SkeletonProps['h']
  noOfLines?: number
}

export const SkeletonText: React.FC<SkeletonTextProps> = (
  props
): JSX.Element => {
  const {
    noOfLines = 3,
    spacing = 2,
    skeletonHeight = 2,
    className,
    startColor,
    endColor,
    isLoaded,
    fadeDuration,
    fadeSpeed,
    children,
    ...rest
  } = props

  const numbers = range(noOfLines)

  const getWidth = (index: number) => {
    if (noOfLines > 1) {
      return index === numbers.length ? '80%' : '100%'
    }
    return '100%'
  }

  const _className = mergeClassnames('mote-skeleton__group', className)

  return (
    <x.div className={_className} {...rest}>
      {numbers.map((number, index) => {
        if (isLoaded && index > 0) {
          return null
        }

        const sizeProps = isLoaded
          ? null
          : {
              mb: number === numbers.length ? 0 : spacing,
              w: getWidth(number),
              h: skeletonHeight
            }

        return (
          <Skeleton
            key={numbers.length.toString() + number}
            startColor={startColor}
            endColor={endColor}
            isLoaded={isLoaded}
            fadeDuration={fadeDuration}
            fadeSpeed={fadeSpeed}
            {...sizeProps}
          >
            {index === 0 ? children : undefined}
          </Skeleton>
        )
      })}
    </x.div>
  )
}

if (__DEV__) {
  SkeletonText.displayName = 'SkeletonText'
}

export interface SkreletonCircleProps extends Omit<SkeletonProps, 'h' | 'w'> {
  size?: SkeletonProps['w']
}

export const SkeletonCircle: React.FC<SkreletonCircleProps> = ({
  size = 24,
  ...rest
}): JSX.Element => (
  <Skeleton
    w={evaluateValue(size)}
    h={evaluateValue(size)}
    {...rest}
    borderRadius='full'
    flexShrink={0}
  />
)

if (__DEV__) {
  SkeletonCircle.displayName = 'SkeletonCircle'
}

const SkeletonRoot = styled.divBox<SkeletonProps>`
  box-shadow: none;
  background-clip: padding-box;
  background-attachment: fixed;
  cursor: default;
  color: transparent;
  pointer-events: none;
  user-select: none;
  opacity: 0.7;

  ${({ fadeSpeed, startColor, endColor }) => css`
    animation: ${fadeSpeed}s linear infinite alternate
      ${fadeColor(startColor as string, endColor as string)};
  `};

  &::before,
  &::after,
  * {
    visibility: hidden;
  }
`

const SkeletonLoadedRoot = styled.divBox`
  ${({ animation }) => css`
    animation: ${animation};
  `};
`
